// #2316 → STAGING-card-rc-b-2026-06-01 — ConfigProvider envelope
// #2409 → LLL re-fetch hook (ConfigHotReloadBridge) — no window.location.reload()
// ----------------------------------------------------------------------------
// Fetches `/v1/runtime-config` on boot, blocks render until resolved,
// exposes config via `useConfig()`. 3-retry exp backoff (1s, 2s, 4s).
// Hot-reload via 30s poll on `configVersion` change (LLL pattern — lazy
// re-fetch + atomic state swap, zero page reload).
//
// ConfigHotReloadBridge: standalone component that drives the hot-reload loop.
// Consumers that need imperative reload can also call `setConfig()` via
// `useConfigUpdater()`.
//
// Back-compat: existing consumers (`OAuthButtons`, `FleetLogin`, etc.) read
// flat cognito fields (`clientId`, `cognitoDomain`, `oauthRedirectUri`,
// `userPoolId`, `region`). Those remain at the root of the returned config
// in addition to the new `cognito.*` sub-envelope. MMM will migrate consumers
// to the sub-envelope in a follow-up; until then both shapes work.
//
// If HHH's backend `/v1/runtime-config` lambda is not yet deployed, the
// provider falls back to inlined defaults (current Cognito values) for local
// dev only and renders a brand-flag warning banner via `configError`.
// ----------------------------------------------------------------------------
import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from "react";
import {
  RuntimeConfig,
  CognitoEnvelope,
  StripeEnvelope,
  SentryEnvelope,
  UmamiEnvelope,
  setRuntimeConfig as setWindowConfig,
} from "../types/RuntimeConfig";
import { initAnalytics } from "../lib/analytics";
import { initSentryFromRuntimeConfig } from "../lib/sentryInit";

// ---------- Types -----------------------------------------------------------
// Re-exported from `types/RuntimeConfig.ts` (sub-card #C, MMM) so any caller —
// including non-React utility modules — can import the envelope shape without
// pulling in this provider.

export type { RuntimeConfig, CognitoEnvelope, StripeEnvelope, SentryEnvelope, UmamiEnvelope };

/**
 * Public context shape. Includes the full envelope + back-compat flat cognito
 * fields so existing consumers continue to compile without changes.
 *
 * `usingFallback` is true when the boot fetch failed and we're rendering with
 * inlined defaults (local-dev convenience; surfaces a warning banner).
 */
export interface ConfigContextValue extends RuntimeConfig {
  /** Back-compat: flat cognito fields. Mirror `cognito.*`. */
  userPoolId:       string;
  clientId:         string;
  region:           string;
  cognitoDomain:    string;
  oauthRedirectUri: string;
  /** True when running with inlined defaults (backend unreachable). */
  usingFallback:    boolean;
}

// ---------- Defaults --------------------------------------------------------

/**
 * Inlined fallback used when `/v1/runtime-config` is unreachable. Values
 * match the live Cognito pool (ap-southeast-2_iU4bemvJr / 2hives-admin hosted-UI domain).
 * The client 3il0fiihh6m31q19fpt3rtfav3 (2hives-spa) on that pool has OAuth code flow
 * enabled with Google + GitHub identity providers and callback https://ai.2bee.farm/callback.
 * #1510 — previously this was pool QZZGn49RM which has no hosted-UI domain.
 */
const FALLBACK_COGNITO: CognitoEnvelope = {
  poolId:   "ap-southeast-2_iU4bemvJr",
  clientId: "3il0fiihh6m31q19fpt3rtfav3",
  region:   "ap-southeast-2",
  domain:   "https://2hives-admin.auth.ap-southeast-2.amazoncognito.com",
};

function buildFallbackEnvelope(apiBaseUrl: string, wsBaseUrl: string): RuntimeConfig {
  return {
    apiBaseUrl,
    wsBaseUrl,
    cognito:       FALLBACK_COGNITO,
    stripe:        { publishableKey: "" },
    sentry:        { dsn: "" },
    umami:         { websiteId: "", host: "" },
    featureFlags:  {},
    buildSha:      "dev-fallback",
    stage:         "dev-fallback",
    configVersion: "fallback-0",
  };
}

function flattenForContext(cfg: RuntimeConfig, usingFallback: boolean): ConfigContextValue {
  return {
    ...cfg,
    userPoolId:       cfg.cognito.poolId,
    clientId:         cfg.cognito.clientId,
    region:           cfg.cognito.region,
    cognitoDomain:    cfg.cognito.domain,
    // #2562 — redirect_uri is ALWAYS derived from the live origin so the same
    // SPA bundle works from 2hives.ai, 2hives.com, and admin.2hives.ai. The
    // Cognito app client allowed-callback-URL list must include each origin
    // (operator action — see PR #2562 checklist).
    oauthRedirectUri: typeof window !== "undefined" ? `${window.location.origin}/callback` : "/callback",
    usingFallback,
  };
}

// ---------- apiBaseUrl derivation ------------------------------------------

/**
 * Derive a bootstrap `apiBaseUrl` from `window.location.hostname` so the very
 * first fetch can find the backend before it knows the canonical URL.
 *
 *   admin.<tenant>     →  https://api.<tenant>
 *   app.<tenant>       →  https://api.<tenant>
 *   try.<tenant>       →  https://api.<tenant>
 *   <tenant> root      →  https://api.<tenant>
 *   localhost / 127.*  →  http://localhost:8000
 */
export function deriveApiBaseUrl(hostname?: string): string {
  const host = hostname ?? (typeof window !== "undefined" ? window.location.hostname : "");
  if (!host || host === "localhost" || host === "127.0.0.1" || host.endsWith(".local")) {
    return "http://localhost:8000";
  }
  // Strip leading admin./app./try./www. → leaves bare tenant; prefix api.
  const stripped = host.replace(/^(admin|app|try|www)\./, "");
  return `https://api.${stripped}`;
}

export function deriveWsBaseUrl(apiBaseUrl: string): string {
  return apiBaseUrl.replace(/^http/, "ws");
}

// ---------- Fetch w/ exp backoff -------------------------------------------

const BACKOFF_MS = [1000, 2000, 4000];

/**
 * Fetch the runtime config with up to 3 retries (1s, 2s, 4s exp backoff).
 * Rejects after 3 consecutive failures. Exposed for tests.
 */
export async function fetchRuntimeConfig(
  apiBaseUrl: string,
  opts: { signal?: AbortSignal; fetchImpl?: typeof fetch } = {},
): Promise<RuntimeConfig> {
  const f = opts.fetchImpl ?? fetch;
  let lastErr: unknown = null;
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      const res = await f(`${apiBaseUrl}/v1/runtime-config`, {
        method: "GET",
        credentials: "omit",
        signal: opts.signal,
        headers: { "Accept": "application/json" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as Partial<RuntimeConfig>;
      // Minimum-viable shape validation; missing fields → throw to trigger retry/fallback.
      if (!data.cognito?.clientId) throw new Error("invalid envelope: missing cognito.clientId");
      return {
        apiBaseUrl:    data.apiBaseUrl    ?? apiBaseUrl,
        wsBaseUrl:     data.wsBaseUrl     ?? deriveWsBaseUrl(apiBaseUrl),
        cognito:       data.cognito,
        stripe:        data.stripe        ?? { publishableKey: "" },
        sentry:        data.sentry        ?? { dsn: "" },
        umami:         data.umami         ?? { websiteId: "", host: "" },
        featureFlags:  data.featureFlags  ?? {},
        buildSha:      data.buildSha      ?? "unknown",
        stage:         data.stage         ?? "unknown",
        configVersion: data.configVersion ?? "0",
      };
    } catch (err) {
      lastErr = err;
      if (attempt === 3) break;
      const delay = BACKOFF_MS[attempt];
      await new Promise<void>((resolve, reject) => {
        const t = setTimeout(resolve, delay);
        opts.signal?.addEventListener("abort", () => { clearTimeout(t); reject(new Error("aborted")); });
      });
    }
  }
  throw lastErr ?? new Error("fetchRuntimeConfig: exhausted retries");
}

// ---------- Context ---------------------------------------------------------

const BOOT_API_BASE = typeof window !== "undefined" ? deriveApiBaseUrl() : "http://localhost:8000";
const BOOT_ENVELOPE = buildFallbackEnvelope(BOOT_API_BASE, deriveWsBaseUrl(BOOT_API_BASE));
const BOOT_FALLBACK = flattenForContext(BOOT_ENVELOPE, true);

// Seed the window snapshot at module-evaluation time so utility modules that
// touch `getRuntimeConfig()` during their own top-level evaluation (e.g.
// `lib/fleetRoles.ts`, `api/userApi.ts`) always see a complete envelope, even
// before `<ConfigProvider>` mounts. `load()` will overwrite this with the
// live config once `/v1/runtime-config` responds. Sub-card #C, MMM.
setWindowConfig(BOOT_ENVELOPE);

const ConfigCtx = createContext<ConfigContextValue>(BOOT_FALLBACK);

/**
 * Updater context — exposes `setConfig()` so `ConfigHotReloadBridge` (and any
 * future imperative caller) can atomically swap in a new config without a page
 * reload. Kept separate from `ConfigCtx` so consumers that only read config
 * don't re-render when the updater reference changes.
 */
export interface ConfigUpdaterContextValue {
  /**
   * Atomically apply a new RuntimeConfig to the provider state.
   * - Mirrors the new envelope onto `window.__2HIVES_CONFIG__`.
   * - Re-initialises analytics if umami websiteId/host changed.
   * - Triggers re-render of all `useConfig()` consumers via context.
   * No-op if the component is unmounted.
   */
  setConfig: (cfg: RuntimeConfig) => void;
}

const ConfigUpdaterCtx = createContext<ConfigUpdaterContextValue>({
  setConfig: () => { /* noop before provider mounts */ },
});

// ---------- Bootstrap UI ----------------------------------------------------

const CENTER_WRAP: React.CSSProperties = {
  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
  minHeight: "100vh", background: "var(--brand-bg-default)", padding: "2rem",
  fontFamily: "sans-serif",
};

const CARD: React.CSSProperties = {
  background: "var(--brand-bg-elevated)", borderRadius: "0.75rem",
  padding: "2rem 2.5rem", maxWidth: 420, width: "90%", textAlign: "center",
};

const SPINNER_KEYFRAMES = `
@keyframes lll-cfg-spin { to { transform: rotate(360deg); } }
`;

function BootstrapLoader() {
  return (
    <div style={CENTER_WRAP} data-testid="config-bootstrap-loader">
      <style>{SPINNER_KEYFRAMES}</style>
      <div style={{ ...CARD, border: "1px solid rgba(247,220,111,0.18)" }}>
        <img
          src="/icon-192.png"
          alt="2hives.ai"
          width={64}
          height={64}
          style={{ display: "block", margin: "0 auto 1.25rem", borderRadius: 12 }}
        />
        <div
          aria-label="Loading"
          style={{
            width: 28, height: 28, margin: "0 auto 0.75rem",
            border: "3px solid rgba(247,220,111,0.18)",
            borderTopColor: "var(--brand-accent)",
            borderRadius: "50%",
            animation: "lll-cfg-spin 0.8s linear infinite",
          }}
        />
        <p style={{ color: "var(--brand-text-secondary)", fontSize: "0.85rem", margin: 0, opacity: 0.8 }}>
          Loading configuration…
        </p>
      </div>
    </div>
  );
}

function BootstrapError({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div style={CENTER_WRAP} data-testid="config-bootstrap-error">
      <div style={{ ...CARD, border: "1px solid rgba(201,64,64,0.3)" }}>
        <div style={{ fontSize: "1.5rem", marginBottom: "0.75rem" }}>⚠</div>
        <h2 style={{ color: "var(--brand-status-error)", margin: "0 0 0.5rem", fontSize: "1rem", fontWeight: 600 }}>
          Unable to load configuration
        </h2>
        <p style={{
          color: "var(--brand-text-secondary)", fontSize: "0.8rem", margin: "0 0 1.25rem",
          opacity: 0.85, whiteSpace: "pre-wrap", wordBreak: "break-word",
        }}>
          {error}
        </p>
        <button
          onClick={onRetry}
          data-testid="config-retry"
          style={{
            background: "rgba(95,166,95,0.15)", border: "1px solid rgba(95,166,95,0.35)",
            color: "var(--brand-status-success)", padding: "0.45rem 1.1rem", borderRadius: "0.4rem",
            cursor: "pointer", fontFamily: "inherit", fontSize: "0.85rem",
          }}
        >
          Retry
        </button>
      </div>
    </div>
  );
}

function FallbackBanner() {
  return (
    <div
      data-testid="config-fallback-banner"
      style={{
        background: "var(--brand-status-warning-bg)",
        color:      "var(--brand-status-warning-text)",
        borderBottom: "1px solid rgba(232,140,0,0.35)",
        padding:    "0.4rem 0.75rem", textAlign: "center",
        fontSize:   "0.8rem", fontFamily: "sans-serif",
      }}
    >
      Running with bootstrap defaults — backend config not reachable.
    </div>
  );
}

// ---------- Provider --------------------------------------------------------

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; config: ConfigContextValue }
  | { kind: "fallback"; config: ConfigContextValue; error: string }   // backend unreachable; using inlined defaults
  | { kind: "error"; error: string };                                  // 3 retries failed; user must Retry

const HOT_RELOAD_POLL_MS = 30_000;

export interface ConfigProviderProps {
  children: ReactNode;
  /** Override fetch impl — used by tests. */
  fetchImpl?: typeof fetch;
  /** Override boot apiBaseUrl — used by tests. */
  bootApiBaseUrl?: string;
  /** Disable the 30s hot-reload poll (used by tests). */
  disableHotReload?: boolean;
  /** Disable inlined fallback (test-only — force the error UI after 3 fails). */
  disableFallback?: boolean;
}

export function ConfigProvider({
  children,
  fetchImpl,
  bootApiBaseUrl,
  disableHotReload,
  disableFallback,
}: ConfigProviderProps) {
  const bootBase = bootApiBaseUrl ?? BOOT_API_BASE;
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const versionRef = useRef<string>("");
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  /**
   * Atomically apply a new config — called by ConfigHotReloadBridge (and
   * available to any imperative caller via useConfigUpdater()).
   * Guards against post-unmount state updates.
   */
  const setConfig = useCallback((cfg: RuntimeConfig) => {
    if (!mountedRef.current) return;
    versionRef.current = cfg.configVersion;
    setWindowConfig(cfg);
    initAnalytics(cfg.umami.websiteId, cfg.umami.host);
    // Card #3095 — Sentry/GlitchTip SDK init. No-op if envelope has no DSN.
    // Idempotent: hot-reloads do not double-init.
    initSentryFromRuntimeConfig();
    setState({ kind: "ready", config: flattenForContext(cfg, false) });
  }, []);

  const load = useCallback(async (signal?: AbortSignal) => {
    setState(prev => prev.kind === "ready" ? prev : { kind: "loading" });
    try {
      // Use the most recently resolved apiBaseUrl if we have one (post-hot-reload),
      // otherwise the derived boot value.
      const base = (state.kind === "ready" || state.kind === "fallback")
        ? state.config.apiBaseUrl
        : bootBase;
      const cfg = await fetchRuntimeConfig(base, { signal, fetchImpl });
      versionRef.current = cfg.configVersion;
      // Mirror onto window snapshot so non-hook modules (api/*.ts, lib/*.ts)
      // can read the envelope without React context. Sub-card #C, MMM.
      setWindowConfig(cfg);
      initAnalytics(cfg.umami.websiteId, cfg.umami.host);
      // Card #3095 — Sentry/GlitchTip SDK init. No-op if envelope has no DSN.
      initSentryFromRuntimeConfig();
      setState({ kind: "ready", config: flattenForContext(cfg, false) });
    } catch (err) {
      if ((err as Error).message === "aborted") return;
      const msg = (err as Error).message || "Unknown error";
      if (disableFallback) {
        setState({ kind: "error", error: msg });
      } else {
        // Local-dev convenience: render with inlined defaults + warning banner.
        const fb = buildFallbackEnvelope(bootBase, deriveWsBaseUrl(bootBase));
        setWindowConfig(fb);
        setState({ kind: "fallback", config: flattenForContext(fb, true), error: msg });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootBase, fetchImpl, disableFallback]);

  // Boot fetch
  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hot-reload poll: every 30s re-fetch; if configVersion changed, propagate
  // via setConfig() — pure LLL state swap, no window.location.reload().
  useEffect(() => {
    if (disableHotReload) return;
    if (state.kind !== "ready" && state.kind !== "fallback") return;
    const base = state.config.apiBaseUrl;
    const t = setInterval(async () => {
      try {
        const cfg = await fetchRuntimeConfig(base, { fetchImpl });
        if (cfg.configVersion !== versionRef.current) {
          setConfig(cfg);
        }
      } catch {
        /* swallow — keep last good config; next tick will retry */
      }
    }, HOT_RELOAD_POLL_MS);
    return () => clearInterval(t);
  }, [state, disableHotReload, fetchImpl, setConfig]);

  if (state.kind === "loading") return <BootstrapLoader />;
  if (state.kind === "error")   return <BootstrapError error={state.error} onRetry={() => load()} />;
  // ready or fallback — both render children; fallback adds the warning banner.
  // ConfigUpdaterCtx is always provided so ConfigHotReloadBridge (and tests)
  // can call setConfig() from anywhere inside the tree.
  return (
    <ConfigUpdaterCtx.Provider value={{ setConfig }}>
      <ConfigCtx.Provider value={state.config}>
        {state.kind === "fallback" && <FallbackBanner />}
        {children}
      </ConfigCtx.Provider>
    </ConfigUpdaterCtx.Provider>
  );
}

// ---------- Hooks ------------------------------------------------------------

export function useConfig(): ConfigContextValue {
  return useContext(ConfigCtx);
}

/**
 * Returns the `setConfig()` function from `ConfigProvider`.
 * Use this to imperatively apply a new `RuntimeConfig` without a page reload.
 * Typically consumed by `ConfigHotReloadBridge`; available to any subtree
 * component that needs to trigger a config swap (e.g., a feature-flag toggle
 * in an admin panel).
 */
export function useConfigUpdater(): ConfigUpdaterContextValue {
  return useContext(ConfigUpdaterCtx);
}

/** Back-compat alias for the old `CognitoConfig` type name (used by E's #2316 code). */
export type CognitoConfig = ConfigContextValue;
